import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import type { components } from '../../api/schema'
import { apiErrorDetail } from '../../lib/apiError'
import { Alert, AlertDescription } from '../ui/alert'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Textarea } from '../ui/textarea'

export type Viewing = components['schemas']['Viewing']
export const VIEWING_KINDS = ['in_person', 'virtual', 'open_house'] as const

/** Refetch everything the viewing and offer flow moves. */
export function useRefreshLetting() {
    const queryClient = useQueryClient()
    return () =>
        queryClient.invalidateQueries({
            predicate: (q) => {
                const [method, path] = q.queryKey as [string, string]
                return (
                    method === 'get' &&
                    typeof path === 'string' &&
                    (path.startsWith('/api/v1/organizations/{orgCode}/viewings') ||
                        path.startsWith('/api/v1/organizations/{orgCode}/rental-applications') ||
                        path.startsWith('/api/v1/organizations/{orgCode}/rental-offers'))
                )
            },
        })
}

/** A local date-time value for an <input type="datetime-local">. */
export function localInput(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * The booking dialog (spec 2026-10-25-12): the slot, the kind, and, on an
 * object under a running lease, the tenant, the pack's notice and the
 * prefilled notice message the manager sends with the booking.
 */
export function BookingDialog({
    org,
    open,
    onOpenChange,
    listingUid,
    assetUid,
    applicationUids,
}: {
    org: string
    open: boolean
    onOpenChange: (open: boolean) => void
    listingUid: string
    assetUid: string
    applicationUids: string[]
}) {
    const { t } = useTranslation('viewings')
    const refresh = useRefreshLetting()
    const [startsAt, setStartsAt] = useState('')
    const [duration, setDuration] = useState('30')
    const [kind, setKind] = useState<string>('in_person')
    const [notify, setNotify] = useState(true)
    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (open) {
            const d = new Date(Date.now() + 3 * 24 * 3600 * 1000)
            d.setHours(10, 0, 0, 0)
            setStartsAt(localInput(d))
            setError(null)
            setNotify(true)
        }
    }, [open])

    const startsIso = startsAt ? new Date(startsAt).toISOString() : ''
    const { data: preview } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/viewings/occupant-notice',
        { params: { path: { orgCode: org }, query: { asset_uid: assetUid, starts_at: startsIso } } },
        { enabled: open && !!startsIso && !!assetUid },
    )
    const notice = preview?.occupant_notice
    useEffect(() => {
        if (notice?.required) {
            setSubject(notice.subject)
            setBody(notice.body)
        }
    }, [notice?.required, notice?.subject, notice?.body])

    const book = $api.useMutation('post', '/api/v1/organizations/{orgCode}/viewings', {
        onSuccess: () => {
            refresh()
            onOpenChange(false)
        },
        onError: (err) => setError(apiErrorDetail(err, t('errors.generic'))),
    })
    const submit = () => {
        const start = new Date(startsAt)
        const end = new Date(start.getTime() + Number(duration) * 60000)
        book.mutate({
            params: { path: { orgCode: org } },
            body: {
                listing_uid: listingUid,
                application_uids: applicationUids,
                starts_at: start.toISOString(),
                ends_at: end.toISOString(),
                kind: kind as 'in_person',
                notify_occupant: !!notice?.required && notify,
                notice_subject: subject,
                notice_body: body,
            },
        })
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="viewing-booking-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t('booking.title')}</DialogTitle>
                    <DialogDescription>{t('booking.description')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <Label className="mb-1 block text-sm">{t('booking.starts_at')}</Label>
                            <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} data-testid="viewing-booking-starts-at" />
                        </div>
                        <div>
                            <Label className="mb-1 block text-sm">{t('booking.duration')}</Label>
                            <Select value={duration} onValueChange={setDuration}>
                                <SelectTrigger data-testid="viewing-booking-duration"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {['15', '30', '45', '60', '90'].map((m) => (
                                        <SelectItem key={m} value={m}>{t('booking.minutes', { count: Number(m) })}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="mb-1 block text-sm">{t('booking.kind')}</Label>
                        <Select value={kind} onValueChange={setKind}>
                            <SelectTrigger data-testid="viewing-booking-kind"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {VIEWING_KINDS.map((k) => (
                                    <SelectItem key={k} value={k} data-testid={`viewing-booking-kind-${k}`}>{t(`kind.${k}`)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {notice?.required ? (
                        <div className="space-y-2 rounded-md border p-3" data-testid="viewing-occupant-notice-preview">
                            <p className="text-sm font-medium">{t('notice.required', { tenant: notice.tenant_name ?? t('notice.tenant_unknown') })}</p>
                            <p className="text-xs text-muted-foreground" data-testid="viewing-occupant-notice-hours">
                                {t('notice.hours', { count: notice.notice_hours })}
                                {notice.legal_basis ? ` · ${notice.legal_basis}` : ''}
                            </p>
                            {notice.inside_window && (
                                <Alert variant="warning" data-testid="viewing-occupant-notice-inside-window">
                                    <AlertDescription>{t('notice.inside_window')}</AlertDescription>
                                </Alert>
                            )}
                            <label className="flex items-center gap-2 text-sm">
                                <Checkbox checked={notify} onCheckedChange={(v) => setNotify(v === true)} data-testid="viewing-occupant-notice-send" />
                                {t('notice.send')}
                            </label>
                            {notify && (
                                <>
                                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="viewing-occupant-notice-subject" />
                                    <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} data-testid="viewing-occupant-notice-body" />
                                </>
                            )}
                        </div>
                    ) : (
                        notice && <p className="text-xs text-muted-foreground" data-testid="viewing-occupant-vacant">{t('notice.vacant')}</p>
                    )}
                    {error && <p className="text-sm text-destructive" data-testid="viewing-booking-error">{error}</p>}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="viewing-booking-cancel">{t('cancel')}</Button>
                    <Button onClick={submit} disabled={!startsAt || book.isPending} data-testid="viewing-booking-confirm">{t('booking.confirm')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
